import { RefreshCw, AlertTriangle, Factory, ShieldCheck, Boxes, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useDashboardDg } from '@/hooks/useBetonSprint6';
import { formatFCFA, formatNumber } from '@/utils/currency';

const fmtPct = (n: number | null) => n == null ? '—' : `${n}%`;

export const DashboardDGPage = () => {
  const { data, isLoading, refetch } = useDashboardDg();

  if (isLoading) {
    return (
      <>
        <Topbar title="Tableau de bord DG" subtitle="Module 15 — Vue consolidée production/qualité/stocks/finance" />
        <div className="ds-content"><div style={{ padding: 32, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement du dashboard…</div></div>
      </>
    );
  }
  if (!data) return null;

  return (
    <>
      <Topbar
        title="Tableau de bord DG"
        subtitle={`Module 15 — Vue consolidée · ${data.periode.jourReference}`}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </>
        }
      />

      <div className="ds-content">
        {/* Alertes globales */}
        {data.alertes.total > 0 && (
          <div className="ds-alert-strip ds-alert-w" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">
              <strong>{data.alertes.total} alerte{data.alertes.total > 1 ? 's' : ''} actives :</strong>{' '}
              {data.alertes.parCategorie.production > 0 && `${data.alertes.parCategorie.production} production · `}
              {data.alertes.parCategorie.qualite > 0    && `${data.alertes.parCategorie.qualite} qualité · `}
              {data.alertes.parCategorie.stocks > 0     && `${data.alertes.parCategorie.stocks} stocks · `}
              {data.alertes.parCategorie.finance > 0    && `${data.alertes.parCategorie.finance} finance`}
            </div>
          </div>
        )}

        {/* ── SECTION 1 — PRODUCTION ── */}
        <SectionTitle icon={<Factory size={16} />} title="Production" />
        <div className="ds-kpi-strip">
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">OF planifiés</div>
            <div className="ds-ks-value">{data.production.ofPlanifies}</div>
            <div className="ds-ks-sub">En attente démarrage</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">OF en cours</div>
            <div className="ds-ks-value">{data.production.ofEnCours}</div>
            <div className="ds-ks-sub">Production active</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">OF terminés (mois)</div>
            <div className="ds-ks-value">{data.production.ofTermines}</div>
            <div className="ds-ks-sub">{data.production.lotsValideMois} lots OK</div>
          </div>
          <div className={`ds-ks ${data.production.ofRetardes > 0 ? 'ks-red' : 'ks-green'}`}>
            <div className="ds-ks-label">OF en retard</div>
            <div className="ds-ks-value">{data.production.ofRetardes}</div>
            <div className="ds-ks-sub">Date prévue dépassée</div>
          </div>
        </div>

        <div className="ds-kpi-strip" style={{ marginTop: 12 }}>
          <div className="ds-ks ks-purple">
            <div className="ds-ks-label">Moules opérationnels</div>
            <div className="ds-ks-value">{data.production.moules.operationnels} / {data.production.moules.total}</div>
            <div className="ds-ks-sub">
              {data.production.moules.maintenance > 0 && `${data.production.moules.maintenance} en maintenance · `}
              {data.production.moules.hors_service > 0 && `${data.production.moules.hors_service} HS`}
            </div>
          </div>
          <div className={`ds-ks ${data.production.moules.a_entretenir > 0 ? 'ks-orange' : 'ks-green'}`}>
            <div className="ds-ks-label">Moules à entretenir</div>
            <div className="ds-ks-value">{data.production.moules.a_entretenir}</div>
            <div className="ds-ks-sub">Seuil cycles dépassé</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Délai moyen OF→livraison</div>
            <div className="ds-ks-value">{data.production.delaiMoyenOfLivraisonJours ?? '—'}</div>
            <div className="ds-ks-sub">jours (mois en cours)</div>
          </div>
          <div className={`ds-ks ${data.production.tauxConformiteLots != null && data.production.tauxConformiteLots >= 97 ? 'ks-green' : 'ks-orange'}`}>
            <div className="ds-ks-label">Taux conformité lots</div>
            <div className="ds-ks-value">{fmtPct(data.production.tauxConformiteLots)}</div>
            <div className="ds-ks-sub">{data.production.lotsValideMois}/{data.production.lotsTotalMois}</div>
          </div>
        </div>

        {/* ── SECTION 2 — QUALITÉ ── */}
        <SectionTitle icon={<ShieldCheck size={16} />} title="Qualité (90j)" />
        <div className="ds-kpi-strip">
          <div className={`ds-ks ${data.qualite.tauxConformite != null && data.qualite.tauxConformite >= data.qualite.objectif ? 'ks-green' : 'ks-red'}`}>
            <div className="ds-ks-label">Taux conformité QC</div>
            <div className="ds-ks-value">{fmtPct(data.qualite.tauxConformite)}</div>
            <div className="ds-ks-sub">Objectif ≥ {data.qualite.objectif}%</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Éprouvettes testées</div>
            <div className="ds-ks-value">{data.qualite.eprouvettes90j.conformes + data.qualite.eprouvettes90j.nonConformes}</div>
            <div className="ds-ks-sub">{data.qualite.eprouvettes90j.conformes} ✓ · {data.qualite.eprouvettes90j.nonConformes} ✗</div>
          </div>
          <div className={`ds-ks ${data.qualite.rebut90j > 0 ? 'ks-orange' : 'ks-green'}`}>
            <div className="ds-ks-label">Lots rejetés</div>
            <div className="ds-ks-value">{data.qualite.rebut90j}</div>
            <div className="ds-ks-sub">Sur 90 jours</div>
          </div>
          <div className={`ds-ks ${data.qualite.dncOuvertes > 0 ? 'ks-red' : 'ks-green'}`}>
            <div className="ds-ks-label">DNC ouvertes</div>
            <div className="ds-ks-value">{data.qualite.dncOuvertes}</div>
            <div className="ds-ks-sub">À traiter</div>
          </div>
        </div>
        {data.qualite.courbeR28.length > 0 && (
          <div className="ds-table-panel" style={{ marginTop: 12 }}>
            <div className="ds-table-header">
              <div>
                <div className="ds-table-title">Courbe fc28 (R28 mesuré vs cible)</div>
                <div className="ds-table-sub">{data.qualite.courbeR28.length} points 90j</div>
              </div>
            </div>
            <div style={{ padding: 12, fontFamily: 'var(--ff-mono)', fontSize: 11, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 6 }}>
              {data.qualite.courbeR28.slice(-15).map((p, i) => {
                const isConform = p.fc28 != null && p.cible != null && p.fc28 >= p.cible;
                return (
                  <div key={i} style={{
                    padding: 6,
                    borderRadius: 4,
                    background: isConform ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                    border: `1px solid ${isConform ? 'var(--success)' : 'var(--danger)'}`,
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{p.date ? new Date(p.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '—'}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isConform ? 'var(--success)' : 'var(--danger)' }}>{p.fc28}</div>
                    <div style={{ fontSize: 9, color: 'var(--tx-3)' }}>/ {p.cible}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── SECTION 3 — STOCKS ── */}
        <SectionTitle icon={<Boxes size={16} />} title="Stocks" />
        <div className="ds-kpi-strip">
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Matières actives</div>
            <div className="ds-ks-value">{data.stocks.actives}</div>
            <div className="ds-ks-sub">Total {data.stocks.total}</div>
          </div>
          <div className={`ds-ks ${data.stocks.alertes > 0 ? 'ks-red' : 'ks-green'}`}>
            <div className="ds-ks-label">Alertes</div>
            <div className="ds-ks-value">{data.stocks.alertes}</div>
            <div className="ds-ks-sub">Sous seuil ou &lt; 7j</div>
          </div>
          <div className="ds-ks ks-purple">
            <div className="ds-ks-label">Valorisation</div>
            <div className="ds-ks-value" style={{ fontSize: 18 }}>{formatFCFA(BigInt(data.stocks.valorisationTotalCents))}</div>
            <div className="ds-ks-sub">CMP × stock</div>
          </div>
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Critiques (ciment/acier)</div>
            <div className="ds-ks-value">{data.stocks.critiques.length}</div>
            <div className="ds-ks-sub">Suivi prioritaire</div>
          </div>
        </div>
        {data.stocks.critiques.length > 0 && (
          <div className="ds-table-panel" style={{ marginTop: 12 }}>
            <div className="ds-table-header">
              <div>
                <div className="ds-table-title">Stocks critiques (ciment + acier)</div>
                <div className="ds-table-sub">Visibilité jours de stock</div>
              </div>
            </div>
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Code</th><th>Nom</th><th>Type</th>
                  <th className="ds-am">Stock</th>
                  <th className="ds-am">Conso/jour</th>
                  <th>Jours restants</th>
                  <th className="ds-am">Valorisation</th>
                </tr>
              </thead>
              <tbody>
                {data.stocks.critiques.map((s) => (
                  <tr key={s.id}>
                    <td><span className="ds-ref-code">{s.code}</span></td>
                    <td>{s.nom}</td>
                    <td style={{ fontSize: 11 }}>{s.type ?? '—'}</td>
                    <td className="ds-am" style={{ color: s.sousSeuil ? 'var(--danger)' : 'inherit', fontWeight: s.sousSeuil ? 600 : 400 }}>
                      {formatNumber(s.stock)} {s.unite}
                    </td>
                    <td className="ds-am" style={{ fontSize: 11 }}>{formatNumber(s.consoJour)}</td>
                    <td style={{ fontSize: 11, color: s.joursRestants != null && s.joursRestants < 7 ? 'var(--warning)' : 'inherit', fontWeight: s.joursRestants != null && s.joursRestants < 7 ? 600 : 400 }}>
                      {s.joursRestants != null ? `${s.joursRestants}j` : '∞'}
                    </td>
                    <td className="ds-am" style={{ fontSize: 11 }}>{s.valorisationCents != null ? formatFCFA(BigInt(s.valorisationCents)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── SECTION 4 — FINANCE ── */}
        <SectionTitle icon={<DollarSign size={16} />} title="Finance" />
        <div className="ds-kpi-strip">
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">CA mois courant</div>
            <div className="ds-ks-value" style={{ fontSize: 18 }}>{formatFCFA(BigInt(data.finance.caMoisCents))}</div>
            <div className="ds-ks-sub" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {data.finance.evolCaPct != null && (data.finance.evolCaPct >= 0
                ? <><TrendingUp size={11} style={{ color: 'var(--success)' }} /> <span style={{ color: 'var(--success)' }}>+{data.finance.evolCaPct}%</span></>
                : <><TrendingDown size={11} style={{ color: 'var(--danger)' }} /> <span style={{ color: 'var(--danger)' }}>{data.finance.evolCaPct}%</span></>
              )}
              {data.finance.evolCaPct != null && ' vs mois précédent'}
            </div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Factures émises</div>
            <div className="ds-ks-value">{data.finance.facturesEmises}</div>
            <div className="ds-ks-sub">{data.finance.facturesPayees} payées mois</div>
          </div>
          <div className={`ds-ks ${data.finance.facturesEnRetard > 0 ? 'ks-red' : 'ks-green'}`}>
            <div className="ds-ks-label">En retard</div>
            <div className="ds-ks-value">{data.finance.facturesEnRetard}</div>
            <div className="ds-ks-sub">Action recouvrement</div>
          </div>
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Encours clients</div>
            <div className="ds-ks-value" style={{ fontSize: 18 }}>{formatFCFA(BigInt(data.finance.encoursClient.totalCents))}</div>
            <div className="ds-ks-sub">À recouvrer</div>
          </div>
        </div>
        <div className="ds-table-panel" style={{ marginTop: 12 }}>
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Balance âgée encours clients</div>
              <div className="ds-table-sub">Restant dû depuis la date d'échéance</div>
            </div>
          </div>
          <div style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            <AgeBucket label="0–30 jours" cents={data.finance.encoursClient.ageBuckets['0_30']} color="var(--success)" />
            <AgeBucket label="30–60 jours" cents={data.finance.encoursClient.ageBuckets['30_60']} color="var(--info)" />
            <AgeBucket label="60–90 jours" cents={data.finance.encoursClient.ageBuckets['60_90']} color="var(--warning)" />
            <AgeBucket label="+ 90 jours"  cents={data.finance.encoursClient.ageBuckets['90_plus']} color="var(--danger)" critique />
          </div>
        </div>
      </div>
    </>
  );
};

const SectionTitle = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8,
    marginTop: 24, marginBottom: 10, paddingBottom: 6,
    borderBottom: '2px solid var(--p)',
    fontSize: 14, fontWeight: 600, color: 'var(--tx-1)',
  }}>
    {icon} {title}
  </div>
);

const AgeBucket = ({ label, cents, color, critique }: { label: string; cents: number; color: string; critique?: boolean }) => (
  <div style={{
    padding: 12, borderRadius: 6,
    border: `2px solid ${color}`,
    background: critique && cents > 0 ? 'rgba(239,68,68,0.05)' : 'transparent',
  }}>
    <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color, marginTop: 4 }}>{formatFCFA(BigInt(cents))}</div>
  </div>
);
